window.theme = window.theme || {};
window.slate = window.slate || {};

theme.Images = (function() {
  /**
   * Preloads an image in memory and uses the browsers cache to store it until needed.
   *
   * @param {Array} images - A list of image urls
   * @param {String} size - A shopify image size attribute
   */

  function preload(images, size) {
    if (typeof images === 'string') {
      images = [images];
    }

    for (var i = 0; i < images.length; i++) {
      var image = images[i];
      this.loadImage(this.getSizedImageUrl(image, size));
    }
  }

  /**
   * Loads and caches an image in the browsers cache.
   * @param {string} path - An image url
   */
  function loadImage(path) {
    new Image().src = path;
  }

  /**
   * Swaps the src of an image for another OR returns the imageURL to the callback function
   * @param image
   * @param element
   * @param callback
   */
  function switchImage(image, element, callback) {
    var size = this.imageSize(element.src);
    var imageUrl = this.getSizedImageUrl(image.src, size);

    if (callback) {
      callback(imageUrl, image, element); // eslint-disable-line callback-return
    } else {
      element.src = imageUrl;
    }
  }

  /**
   * +++ Useful
   * Find the Shopify image attribute size
   *
   * @param {string} src
   * @returns {null}
   */
  function imageSize(src) {
    var match = src.match(
      /.+_((?:pico|icon|thumb|small|compact|medium|large|grande)|\d{1,4}x\d{0,4}|x\d{1,4})[_\\.@]/
    );

    if (match === null) {
      return null;
    } else {
      return match[1];
    }
  }

  /**
   * +++ Useful
   * Adds a Shopify size attribute to a URL
   *
   * @param src
   * @param size
   * @returns {*}
   */
  function getSizedImageUrl(src, size) {
    if (size === null) {
      return src;
    }

    if (size === 'master') {
      return this.removeProtocol(src);
    }

    var match = src.match(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i
    );

    if (match !== null) {
      var prefix = src.split(match[0]);
      var suffix = match[0];

      return this.removeProtocol(prefix[0] + '_' + size + suffix);
    }

    return null;
  }

  function removeProtocol(path) {
    return path.replace(/http(s)?:/, '');
  }

  return {
    preload: preload,
    loadImage: loadImage,
    switchImage: switchImage,
    imageSize: imageSize,
    getSizedImageUrl: getSizedImageUrl,
    removeProtocol: removeProtocol
  };
})();

/**
 * Variant Selection scripts
 * ------------------------------------------------------------------------------
 *
 * Handles change events from the variant inputs in any `cart/add` forms that may
 * exist.  Also updates the master select and triggers updates when the variants
 * price or image changes.
 *
 * @namespace variants
 */

slate.Variants = (function() {
  /**
   * Variant constructor
   *
   * @param {object} options - Settings from `product.js`
   */
  function Variants(options) {
    this.container = options.container;
    this.product = options.product;
    this.singleOptionSelector = options.singleOptionSelector;
    this.originalSelectorId = options.originalSelectorId;
    this.enableHistoryState = options.enableHistoryState;
    this.currentVariant = this._getVariantFromOptions();

    var self = this;
    this._onSelectChangeHandler = this._onSelectChange.bind(this);
    this.container.querySelectorAll(this.singleOptionSelector).forEach(function(el) {
      el.addEventListener('change', self._onSelectChangeHandler);
    });
  }

  Variants.prototype = Object.assign({}, Variants.prototype, {
    /**
     * Get the currently selected options from add-to-cart form. Works with all
     * form input elements.
     *
     * @return {array} options - Values of currently selected variants
     */
    _getCurrentOptions: function() {
      var elements = this.container.querySelectorAll(this.singleOptionSelector);
      var currentOptions = Array.prototype.map.call(elements, function(element) {
        var type = element.type;
        var currentOption = {};

        if (type === 'radio' || type === 'checkbox') {
          if (element.checked) {
            currentOption.value = element.value;
            currentOption.index = element.dataset.index;

            return currentOption;
          } else {
            return false;
          }
        } else {
          currentOption.value = element.value;
          currentOption.index = element.dataset.index;

          return currentOption;
        }
      });

      // remove any unchecked input values if using radio buttons or checkboxes
      currentOptions = currentOptions.filter(Boolean);

      return currentOptions;
    },

    /**
     * Find variant based on selected values.
     *
     * @param  {array} selectedValues - Values of variant inputs
     * @return {object || undefined} found - Variant object from product.variants
     */
    _getVariantFromOptions: function() {
      var selectedValues = this._getCurrentOptions();
      var variants = this.product.variants;

      // variant[values.index] (option1/option2/option3) and values.value (a
      // form field's .value) are always plain strings here, so a strict
      // equality check is equivalent to lodash's deep-equal isEqual --
      // deep comparison only matters for objects/arrays, neither of which
      // occurs in this comparison.
      var found = variants.find(function(variant) {
        return selectedValues.every(function(values) {
          return variant[values.index] === values.value;
        });
      });

      return found;
    },

    /**
     * Event handler for when a variant input changes.
     */
    _onSelectChange: function() {
      var variant = this._getVariantFromOptions();

      this.container.dispatchEvent(
        new CustomEvent('variantChange', { detail: { variant: variant } })
      );

      if (!variant) {
        return;
      }

      this._updateMasterSelect(variant);
      this._updateImages(variant);
      this._updatePrice(variant);
      this._updateSKU(variant);
      this.currentVariant = variant;

      if (this.enableHistoryState) {
        this._updateHistoryState(variant);
      }
    },

    /**
     * Trigger event when variant image changes
     *
     * @param  {object} variant - Currently selected variant
     * @return {event}  variantImageChange
     */
    _updateImages: function(variant) {
      var variantImage = variant.featured_image || {};
      var currentVariantImage = this.currentVariant.featured_image || {};

      if (
        !variant.featured_image ||
        variantImage.src === currentVariantImage.src
      ) {
        return;
      }

      this.container.dispatchEvent(
        new CustomEvent('variantImageChange', { detail: { variant: variant } })
      );
    },

    /**
     * Trigger event when variant price changes.
     *
     * @param  {object} variant - Currently selected variant
     * @return {event} variantPriceChange
     */
    _updatePrice: function(variant) {
      if (
        variant.price === this.currentVariant.price &&
        variant.compare_at_price === this.currentVariant.compare_at_price
      ) {
        return;
      }

      this.container.dispatchEvent(
        new CustomEvent('variantPriceChange', { detail: { variant: variant } })
      );
    },

    /**
     * Trigger event when variant sku changes.
     *
     * @param  {object} variant - Currently selected variant
     * @return {event} variantSKUChange
     */
    _updateSKU: function(variant) {
      if (variant.sku === this.currentVariant.sku) {
        return;
      }

      this.container.dispatchEvent(
        new CustomEvent('variantSKUChange', { detail: { variant: variant } })
      );
    },

    /**
     * Update history state for product deeplinking
     *
     * @param  {variant} variant - Currently selected variant
     * @return {k}         [description]
     */
    _updateHistoryState: function(variant) {
      if (!history.replaceState || !variant) {
        return;
      }

      var newurl =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname +
        '?variant=' +
        variant.id;
      window.history.replaceState({ path: newurl }, '', newurl);
    },

    /**
     * Update hidden master select of variant change
     *
     * @param  {variant} variant - Currently selected variant
     */
    _updateMasterSelect: function(variant) {
      var masterSelect = this.container.querySelector(this.originalSelectorId);
      if (masterSelect) masterSelect.value = variant.id;
    },

    unbind: function() {
      var self = this;
      this.container.querySelectorAll(this.singleOptionSelector).forEach(function(el) {
        el.removeEventListener('change', self._onSelectChangeHandler);
      });
    }
  });

  return Variants;
})();

window.Modals = (function() {
  var Modal = function(id, name, options) {
    var defaults = {
      close: '.js-modal-close',
      open: '.js-modal-open-' + name,
      openClass: 'modal--is-active'
    };

    this.modal = document.getElementById(id);

    if (!this.modal) {
      return;
    }

    this.body = document.body;
    this.config = Object.assign({}, defaults, options);

    this.modalIsOpen = false;
    this.focusOnOpen = this.config.focusOnOpen
      ? document.querySelector(this.config.focusOnOpen)
      : this.modal;

    this._onKeyupHandler = this._onKeyup.bind(this);

    this.init();
  };

  Modal.prototype.init = function() {
    var self = this;

    document.querySelectorAll(this.config.open).forEach(function(btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', self.open.bind(self));
    });

    this.modal.querySelectorAll(this.config.close).forEach(function(btn) {
      btn.addEventListener('click', self.close.bind(self));
    });
  };

  Modal.prototype.open = function(evt) {
    // Keep track if modal was opened from a click, or called by another function
    var externalCall = false;

    // don't open an opened modal
    if (this.modalIsOpen) {
      return;
    }

    // Prevent following href if link is clicked
    if (evt) {
      evt.preventDefault();
    } else {
      externalCall = true;
    }

    // Without this, the modal opens, the click event bubbles up to $nodes.page
    // which closes the modal.
    if (evt && evt.stopPropagation) {
      evt.stopPropagation();
      // save the source of the click, we'll focus to this on close
      this.activeSource = evt.currentTarget;
    }

    if (this.modalIsOpen && !externalCall) {
      return this.close();
    }

    theme.prepareTransition(this.modal);
    this.modal.classList.add(this.config.openClass);
    this.body.classList.add(this.config.openClass);

    this.modalIsOpen = true;

    // Set focus on modal
    slate.a11y.trapFocus({
      container: this.modal,
      namespace: 'modal_focus',
      elementToFocus: this.focusOnOpen
    });

    if (this.activeSource && this.activeSource.getAttribute('aria-expanded') !== null) {
      this.activeSource.setAttribute('aria-expanded', 'true');
    }

    this.bindEvents();
  };

  Modal.prototype.close = function() {
    // don't close a closed modal
    if (!this.modalIsOpen) {
      return;
    }

    // deselect any focused form elements
    if (document.activeElement) document.activeElement.blur();

    theme.prepareTransition(this.modal);
    this.modal.classList.remove(this.config.openClass);
    this.body.classList.remove(this.config.openClass);

    this.modalIsOpen = false;

    // Remove focus on modal
    slate.a11y.removeTrapFocus({
      container: this.modal,
      namespace: 'modal_focus'
    });

    if (this.activeSource && this.activeSource.getAttribute('aria-expanded') !== null) {
      this.activeSource.setAttribute('aria-expanded', 'false');
      this.activeSource.focus();
    }

    this.unbindEvents();
  };

  Modal.prototype.bindEvents = function() {
    // Pressing escape closes modal
    this.body.addEventListener('keyup', this._onKeyupHandler);
  };

  Modal.prototype._onKeyup = function(evt) {
    if (evt.keyCode === 27) {
      this.close();
    }
  };

  Modal.prototype.unbindEvents = function() {
    this.body.removeEventListener('keyup', this._onKeyupHandler);
  };

  return Modal;
})();

window.AjaxCart = (function() {
  var cart = function(form) {
    this.form = form;
    this.cartIconIndicator = document.querySelector('.site-header-cart-indicator');

    this._onSubmitHandler = this.addItemFromForm.bind(this);

    if (this.form) {
      this.form.addEventListener('submit', this._onSubmitHandler);
    }
  };

  cart.prototype.addItemFromForm = function(evt) {
    evt.preventDefault();

    var self = this;
    // FormData (rather than a urlencoded serialize) so any file inputs in
    // the form -- eg. the crossgrade/education proof upload -- are actually
    // included in the request instead of being silently dropped.
    var formData = new FormData(this.form);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function(res) {
        return res.json().then(function(data) {
          if (!res.ok) {
            var error = new Error(data.message || 'Add to cart request failed');
            error.data = data;
            throw error;
          }
          return data;
        });
      })
      .then(function(lineItem) {
        self.success(lineItem);
      })
      .catch(function(err) {
        self.error(err);
      });
  };

  cart.prototype.success = function() {
    theme.Notify.open('success', false, true);

    // Update cart notification bubble's state
    if (this.cartIconIndicator) {
      this.cartIconIndicator.classList.remove('hide');
    }
  };

  // Error handling reference from Shopify.onError in api.jquery.js
  cart.prototype.error = function(err) {
    var data = err && err.data;

    if (data && data.message) {
      theme.Notify.open('error', data.description, true);
    }
  };

  return cart;
})();

theme.Product = (function() {
  var defaults = {
    ajaxCart: true,
    stockSetting: false
  };

  function debounce(delay, fn) {
    var timer;
    return function() {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function() {
        fn.apply(null, args);
      }, delay);
    };
  }

  function toggleDisplay(selector, shouldShow) {
    var el = document.querySelector(selector);
    if (!el) return;
    el.style.display = shouldShow ? '' : 'none';
  }

  function Product(container) {
    this.container = container;
    var sectionId = container.getAttribute('data-section-id');

    this.selectors = {
      originalSelectorId: '#ProductSelect-' + sectionId,
      modal: 'ProductModal',
      productZoomImage: '#ProductZoomImg',
      addToCart: '#AddToCart-' + sectionId,
      productPrice: '#ProductPrice-' + sectionId,
      comparePrice: '#ComparePrice-' + sectionId,
      addToCartText: '#AddToCartText-' + sectionId,
      SKU: '.variant-sku',
      productFeaturedImage: '#product-featured-image-' + sectionId,
      productImageWrap: '#product-image-link-' + sectionId,
      productThumbAllImages: '.product-thumbnail',
      productThumbImages: '.product-thumbnail-' + sectionId,
      productThumbs: '#product-thumbnails-' + sectionId,
      saleTag: '#ProductSaleTag-' + sectionId,
      productStock: '#ProductStock-' + sectionId,
      singleOptionSelector: '.single-option-selector-' + sectionId
    };

    var featuredImageEl = document.querySelector(this.selectors.productFeaturedImage);

    this.settings = Object.assign({}, defaults, {
      sectionId: sectionId,
      ajaxCart: container.dataset.ajax === 'true',
      stockSetting: container.dataset.stock === 'true',
      enableHistoryState: container.dataset.enableHistoryState === 'true',
      namespace: '.product-' + sectionId,
      imageSize: theme.Images.imageSize(
        featuredImageEl ? featuredImageEl.getAttribute('src') : ''
      )
    });

    var productJsonEl = document.getElementById('ProductJson-' + sectionId);

    // Stop parsing if we don't have the product json script tag
    if (!productJsonEl || !productJsonEl.innerHTML) {
      return;
    }

    this.productSingleObject = JSON.parse(productJsonEl.innerHTML);
    this.addVariantInfo();

    this.init();
  }

  Product.prototype = Object.assign({}, Product.prototype, {
    init: function() {
      this._stringOverrides();
      this._initVariants();
      this._productZoomImage();
      this._productThumbSwitch();
      this._productThumbnailSlider();

      if (this.settings.ajaxCart) {
        theme.AjaxCart = new window.AjaxCart(document.getElementById('AddToCartForm'));
      }

      // Pre-loading product images to avoid a lag when a thumbnail is clicked, or
      // when a variant is selected that has a variant image.
      theme.Images.preload(
        this.productSingleObject.images,
        this.settings.imageSize
      );
    },

    _stringOverrides: function() {
      window.productStrings = window.productStrings || {};
      Object.assign(theme.strings, window.productStrings);
    },

    addVariantInfo: function() {
      if (!this.productSingleObject || !this.settings.stockSetting) {
        return;
      }

      var variantJsonEl = document.getElementById('VariantJson-' + this.settings.sectionId);
      if (!variantJsonEl) return;

      var variantInfo = JSON.parse(variantJsonEl.innerHTML);

      for (var i = 0; i < variantInfo.length; i++) {
        Object.assign(this.productSingleObject.variants[i], variantInfo[i]);
      }
    },

    _initVariants: function() {
      var options = {
        container: this.container,
        enableHistoryState: this.settings.enableHistoryState,
        product: this.productSingleObject,
        singleOptionSelector: this.selectors.singleOptionSelector,
        originalSelectorId: this.selectors.originalSelectorId
      };

      this.variants = new slate.Variants(options);

      // slate.Variants dispatches CustomEvents with the variant on
      // evt.detail -- unwrap here so the handlers below can share their
      // {variant} shape with the direct calls just below.
      var self = this;
      this._onVariantChangeHandler = function(evt) { self._updateAddToCartBtn(evt.detail); };
      this._onVariantPriceChangeHandler = function(evt) { self._updatePrice(evt.detail); };
      this._onVariantSKUChangeHandler = function(evt) { self._updateSKU(evt.detail); };
      this._onVariantImageChangeHandler = function(evt) { self._updateImages(evt.detail); };

      this.container.addEventListener('variantChange', this._onVariantChangeHandler);
      this.container.addEventListener('variantPriceChange', this._onVariantPriceChangeHandler);
      this.container.addEventListener('variantSKUChange', this._onVariantSKUChangeHandler);
      this.container.addEventListener('variantImageChange', this._onVariantImageChangeHandler);

      // Sync all variant-dependent UI to the currently selected variant on
      // page load/refresh. These handlers are normally only triggered by
      // _onSelectChange, which fires from a user-driven 'change' event —
      // so without this, everything below stays stuck on whatever the
      // initial page markup happened to render.
      if (this.variants.currentVariant) {
        var currentVariantEvt = { variant: this.variants.currentVariant };
        this._updateAddToCartBtn(currentVariantEvt);
        this._updatePrice(currentVariantEvt);
        this._updateSKU(currentVariantEvt);
        this._updateImages(currentVariantEvt);
      }
    },

    _updateAddToCartBtn: function(evt) {
      var variant = evt.variant;

      var stockEl = document.querySelector(this.selectors.productStock);
      var addToCartEl = document.querySelector(this.selectors.addToCart);
      var addToCartTextEl = document.querySelector(this.selectors.addToCartText);

      /* PF ADDED THIS */

      if (variant) {
        var affirmEl = document.querySelector('.affirm-as-low-as');
        if (affirmEl) affirmEl.setAttribute('data-amount', variant.price);
        if (typeof affirm !== 'undefined' && affirm.ui && typeof affirm.ui.refresh === 'function') {
          // affirm.ui.refresh() can throw internally (eg. when third-party
          // cookies are blocked) -- that must never take down the rest of
          // this function (add-to-cart button state, stock display, etc).
          try {
            affirm.ui.refresh();
          } catch (err) {
            console.error('[theme.Product] affirm.ui.refresh() threw:', err);
          }
        }

        var klarnaEl = document.querySelector('klarna-placement');
        if (klarnaEl) klarnaEl.setAttribute('data-purchase-amount', variant.price);
        try {
          window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
          window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
        } catch (err) {
          console.error('[theme.Product] KlarnaOnsiteService.push() threw:', err);
        }

        toggleDisplay(
          '#eduform',
          variant.title.includes('Education') || this.productSingleObject.title.includes('Education')
        );
        toggleDisplay('#presonus-crossgrade', variant.title.includes('Crossgrade'));
        toggleDisplay('#steinberg-crossgrade', variant.title.includes('Crossgrade'));
        toggleDisplay('#audio-modeling-upgrade', variant.title === 'Upgrade from V2 to V3');
        /* END PF CODE */

        // Select a valid variant if available
        if (variant.available) {
          // We have a valid product variant, so enable the submit button
          if (addToCartEl) {
            addToCartEl.classList.remove('btn--sold-out');
            addToCartEl.disabled = false;
          }
          if (addToCartTextEl) addToCartTextEl.innerHTML = theme.strings.addToCart;
          // Show how many items are left, if below 10
          if (this.settings.stockSetting && stockEl) {
            if (variant.inventory_management) {
              if (
                variant.inventory_quantity < 10 &&
                variant.inventory_quantity > 0
              ) {
                stockEl.innerHTML = theme.strings.stockAvailable.replace(
                  '1',
                  variant.inventory_quantity
                );
                stockEl.classList.remove('hide');
              } else if (variant.inventory_quantity <= 0 && variant.incoming) {
                stockEl.innerHTML = theme.strings.willNotShipUntil.replace(
                  '[date]',
                  variant.next_incoming_date
                );
                stockEl.classList.remove('hide');
              } else {
                stockEl.classList.add('hide');
              }
            } else {
              stockEl.classList.add('hide');
            }
          }
        } else {
          // Variant is sold out, disable the submit button
          if (addToCartEl) {
            addToCartEl.disabled = true;
            addToCartEl.classList.add('btn--sold-out');
          }
          if (addToCartTextEl) addToCartTextEl.innerHTML = theme.strings.soldOut;
          if (this.settings.stockSetting && stockEl) {
            if (variant.incoming) {
              stockEl.innerHTML = theme.strings.willBeInStockAfter.replace(
                '[date]',
                variant.next_incoming_date
              );
              stockEl.classList.remove('hide');
            } else {
              stockEl.classList.add('hide');
            }
          }
        }
      } else {
        if (addToCartEl) {
          addToCartEl.disabled = true;
          addToCartEl.classList.remove('btn--sold-out');
        }
        if (addToCartTextEl) addToCartTextEl.innerHTML = theme.strings.unavailable;
        if (this.settings.stockSetting && stockEl) {
          stockEl.classList.add('hide');
        }
      }
    },

    _updatePrice: function(evt) {
      var variant = evt.variant;
      var priceEl = document.querySelector(this.selectors.productPrice);
      var comparePriceEl = document.querySelector(this.selectors.comparePrice);
      var saleTagEl = document.querySelector(this.selectors.saleTag);
      var newDiscountEls = document.querySelectorAll('.newDiscount');

      if (variant) {
        if (priceEl) {
          priceEl.innerHTML = theme.Currency.formatMoney(variant.price, theme.moneyFormat);
        }

        // PF ADDED THIS CODE
        if (variant.compare_at_price > variant.price) {
          var discountPercent = (
            ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
          ).toFixed(0);
          newDiscountEls.forEach(function(el) {
            el.style.display = '';
            el.innerHTML = discountPercent + '% off';
          });
        } else {
          newDiscountEls.forEach(function(el) {
            el.style.display = 'none';
          });
        }
        // END PF CODE

        // Update and show the product's compare price if necessary
        if (variant.compare_at_price > variant.price) {
          if (comparePriceEl) {
            comparePriceEl.innerHTML = theme.Currency.formatMoney(
              variant.compare_at_price,
              theme.moneyFormat
            );
            comparePriceEl.classList.remove('hide');
          }
          if (saleTagEl) saleTagEl.classList.remove('hide');
        } else {
          if (comparePriceEl) comparePriceEl.classList.add('hide');
          if (saleTagEl) saleTagEl.classList.add('hide');
        }
      } else {
        if (comparePriceEl) comparePriceEl.classList.add('hide');
      }
    },

    _updateSKU: function(evt) {
      var variant = evt.variant;

      if (variant) {
        document.querySelectorAll(this.selectors.SKU).forEach(function(el) {
          el.innerHTML = variant.sku;
        });

        /* PF ADDED THIS */
        var inventoryLevel = typeof inv_qty !== 'undefined' ? inv_qty[variant.id] : undefined;
        var manuallyEl = document.getElementById('manually');
        var instantDeliveryEls = document.querySelectorAll('.instant-delivery');

        if (inventoryLevel > 0) {
          if (manuallyEl) manuallyEl.style.display = 'none';
          instantDeliveryEls.forEach(function(el) { el.style.display = ''; });
        } else if (inventoryLevel < 1) {
          if (manuallyEl) manuallyEl.style.display = '';
          instantDeliveryEls.forEach(function(el) { el.style.display = 'none'; });
        }
        /* END PF CODE */
      }
    },

    _updateImages: function(evt) {
      var variant = evt.variant;

      if (variant && variant.featured_image) {
        var sizedImgUrl = theme.Images.getSizedImageUrl(
          variant.featured_image.src,
          this.settings.imageSize
        );
        var largeImgUrl = theme.Images.getSizedImageUrl(
          variant.featured_image.src,
          '1024x1024'
        );
        this.switchProductImage(sizedImgUrl, largeImgUrl);
        this.setActiveThumbnail();
      }
    },

    switchProductImage: function(image, largeImage) {
      var featuredImageEl = document.querySelector(this.selectors.productFeaturedImage);
      var imageWrapEl = document.querySelector(this.selectors.productImageWrap);
      if (featuredImageEl) featuredImageEl.setAttribute('src', image);
      if (imageWrapEl) imageWrapEl.setAttribute('href', largeImage);
    },

    setActiveThumbnail: function(el) {
      var thumbsEl = document.querySelector(this.selectors.productThumbs);

      // If there is no element passed, find it by the current product image
      if (typeof el === 'undefined') {
        var featuredImageEl = document.querySelector(this.selectors.productFeaturedImage);
        var src = featuredImageEl ? featuredImageEl.getAttribute('src') : null;
        el = src ? document.querySelector('.product-thumbnail[href="' + src + '"]') : null;
      }

      // Set active thumbnail class
      if (thumbsEl) {
        thumbsEl.querySelectorAll('.product-thumbnail-item').forEach(function(item) {
          item.classList.remove('is-active');
        });
      }
      if (el && el.parentElement) {
        el.parentElement.classList.add('is-active');
      }

      // If the thumbnail strip scrolls (more than 2 thumbnails), bring the
      // newly active one into view.
      if (thumbsEl && thumbsEl.classList.contains('product-thumbnails--scroller') && el && el.parentElement) {
        el.parentElement.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      }
    },

    _productZoomImage: function() {
      var featuredImageEl = document.querySelector(this.selectors.productFeaturedImage);
      if (!featuredImageEl) {
        return;
      }

      var self = this;
      var imageWrapEl = document.querySelector(this.selectors.productImageWrap);

      this._onImageWrapClickHandler = function(evt) {
        evt.preventDefault();
        // Empty src before loading new image to avoid awkward image swap
        var zoomImgEl = document.querySelector(self.selectors.productZoomImage);
        if (zoomImgEl) {
          zoomImgEl.setAttribute('src', '');
          zoomImgEl.setAttribute('src', evt.currentTarget.getAttribute('href'));
        }
      };

      if (imageWrapEl) {
        imageWrapEl.addEventListener('click', this._onImageWrapClickHandler);
      }

      this.ProductModal = new window.Modals(this.selectors.modal, 'product-modal');

      // Close modal if clicked, but not if the image is clicked
      if (this.ProductModal.modal) {
        this._onModalClickHandler = function(evt) {
          if (evt.target.nodeName !== 'IMG') {
            self.ProductModal.close();
          }
        };
        this.ProductModal.modal.addEventListener('click', this._onModalClickHandler);
      }
    },

    _productThumbSwitch: function() {
      var thumbsWrapperEl = document.querySelector(this.selectors.productThumbs);
      if (!thumbsWrapperEl) {
        return;
      }

      var self = this;
      this._onThumbClickHandler = function(evt) {
        evt.preventDefault();
        var link = evt.currentTarget;
        var sizedImageUrl = link.getAttribute('href');
        var largeImageUrl = link.getAttribute('data-zoom');
        self.setActiveThumbnail(link);
        self.switchProductImage(sizedImageUrl, largeImageUrl);
      };

      document.querySelectorAll(this.selectors.productThumbImages).forEach(function(el) {
        el.addEventListener('click', self._onThumbClickHandler);
      });
    },

    /*
      Thumbnail slider

      Replaces the old Slick-carousel-based slider (a jQuery plugin) with a
      native horizontally-scrolling strip: overflow-x scroll/scroll-snap for
      the actual scrolling (handles mouse-wheel, trackpad, and touch drag for
      free), plus a pair of arrow buttons that page by the strip's visible
      width. See product-new.css.liquid for the accompanying styles.
     */
    _productThumbnailSlider: function() {
      var thumbsWrapperEl = document.querySelector(this.selectors.productThumbs);
      if (!thumbsWrapperEl) {
        return;
      }

      var thumbnailCount = thumbsWrapperEl.querySelectorAll(
        this.selectors.productThumbAllImages
      ).length;

      if (thumbnailCount > 2) {
        thumbsWrapperEl.classList.add('product-thumbnails--scroller');
        this._buildThumbnailArrows(thumbsWrapperEl);
        this._updateThumbnailArrowState();
        this._productSwipeInit();

        var self = this;
        this._onThumbsScrollHandler = debounce(100, function() {
          self._updateThumbnailArrowState();
        });
        thumbsWrapperEl.addEventListener('scroll', this._onThumbsScrollHandler, { passive: true });

        // Bring the initially-active thumbnail into view (no smooth-scroll
        // animation on first load, same as the old slickGoTo(..., true)).
        var activeEl = thumbsWrapperEl.querySelector('.is-active');
        if (activeEl) {
          activeEl.scrollIntoView({ inline: 'nearest', block: 'nearest' });
        }
      }
    },

    _buildThumbnailArrows: function(thumbsWrapperEl) {
      var wrapEl = document.createElement('div');
      wrapEl.className = 'product-thumbnails-wrap';
      thumbsWrapperEl.parentNode.insertBefore(wrapEl, thumbsWrapperEl);
      wrapEl.appendChild(thumbsWrapperEl);

      var prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'product-thumbnails__arrow product-thumbnails__arrow--prev';
      prevBtn.setAttribute('aria-label', 'Previous thumbnails');

      var nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'product-thumbnails__arrow product-thumbnails__arrow--next';
      nextBtn.setAttribute('aria-label', 'Next thumbnails');

      prevBtn.addEventListener('click', function() {
        thumbsWrapperEl.scrollBy({ left: -thumbsWrapperEl.clientWidth, behavior: 'smooth' });
      });
      nextBtn.addEventListener('click', function() {
        thumbsWrapperEl.scrollBy({ left: thumbsWrapperEl.clientWidth, behavior: 'smooth' });
      });

      wrapEl.insertBefore(prevBtn, thumbsWrapperEl);
      wrapEl.appendChild(nextBtn);

      this._thumbnailPrevBtn = prevBtn;
      this._thumbnailNextBtn = nextBtn;
    },

    _updateThumbnailArrowState: function() {
      var thumbsWrapperEl = document.querySelector(this.selectors.productThumbs);
      if (!thumbsWrapperEl) return;

      if (this._thumbnailPrevBtn) {
        this._thumbnailPrevBtn.disabled = thumbsWrapperEl.scrollLeft <= 0;
      }
      if (this._thumbnailNextBtn) {
        var maxScroll = thumbsWrapperEl.scrollWidth - thumbsWrapperEl.clientWidth;
        this._thumbnailNextBtn.disabled = thumbsWrapperEl.scrollLeft >= maxScroll - 1;
      }
    },

    // Swiping left/right on the main product image steps to the
    // next/previous thumbnail (single-step, wraps around) -- distinct from
    // the arrow buttons above, which page the thumbnail strip itself.
    _productSwipeInit: function() {
      var featuredImageEl = document.querySelector(this.selectors.productFeaturedImage);
      if (!featuredImageEl) return;

      var self = this;
      var touchStartX = null;
      var touchStartY = null;
      var swipeThreshold = 30;

      this._onTouchStartHandler = function(evt) {
        var touch = evt.touches && evt.touches[0];
        if (!touch) return;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      };

      this._onTouchEndHandler = function(evt) {
        if (touchStartX === null) return;
        var touch = evt.changedTouches && evt.changedTouches[0];
        if (!touch) return;

        var deltaX = touch.clientX - touchStartX;
        var deltaY = touch.clientY - touchStartY;
        touchStartX = null;
        touchStartY = null;

        // Only treat it as a swipe if it's clearly more horizontal than
        // vertical, so vertical page-scrolling isn't hijacked.
        if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) < Math.abs(deltaY)) {
          return;
        }

        if (deltaX < 0) {
          self._goToNextThumbnail();
        } else {
          self._goToPrevThumbnail();
        }
      };

      featuredImageEl.addEventListener('touchstart', this._onTouchStartHandler, { passive: true });
      featuredImageEl.addEventListener('touchend', this._onTouchEndHandler, { passive: true });
    },

    _goToNextThumbnail: function() {
      this._stepThumbnail(1);
    },

    _goToPrevThumbnail: function() {
      this._stepThumbnail(-1);
    },

    _stepThumbnail: function(direction) {
      var thumbsWrapperEl = document.querySelector(this.selectors.productThumbs);
      if (!thumbsWrapperEl) return;

      var items = Array.prototype.slice.call(thumbsWrapperEl.querySelectorAll('.product-thumbnail-item'));
      if (!items.length) return;

      var currentIndex = items.findIndex(function(item) {
        return item.classList.contains('is-active');
      });

      var nextIndex = currentIndex + direction;
      if (nextIndex < 0) nextIndex = items.length - 1;
      if (nextIndex >= items.length) nextIndex = 0;

      var nextLink = items[nextIndex].querySelector('.product-thumbnail');
      if (nextLink) {
        // Reuses the normal click handler (_onThumbClickHandler) so the
        // main image, active state, and scroll-into-view all update the
        // same way a real click would.
        nextLink.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }
    },

    onUnload: function() {
      var imageWrapEl = document.querySelector(this.selectors.productImageWrap);
      if (imageWrapEl && this._onImageWrapClickHandler) {
        imageWrapEl.removeEventListener('click', this._onImageWrapClickHandler);
      }

      if (this._onThumbClickHandler) {
        var self = this;
        document.querySelectorAll(this.selectors.productThumbImages).forEach(function(el) {
          el.removeEventListener('click', self._onThumbClickHandler);
        });
      }

      var thumbsEl = document.querySelector(this.selectors.productThumbs);
      if (thumbsEl && this._onThumbsScrollHandler) {
        thumbsEl.removeEventListener('scroll', this._onThumbsScrollHandler);
      }

      var featuredImageEl = document.querySelector(this.selectors.productFeaturedImage);
      if (featuredImageEl) {
        if (this._onTouchStartHandler) featuredImageEl.removeEventListener('touchstart', this._onTouchStartHandler);
        if (this._onTouchEndHandler) featuredImageEl.removeEventListener('touchend', this._onTouchEndHandler);
      }

      if (this.ProductModal && this.ProductModal.modal && this._onModalClickHandler) {
        this.ProductModal.modal.removeEventListener('click', this._onModalClickHandler);
      }

      if (this.variants) {
        this.variants.unbind();
      }
    }
  });

  return Product;
})();
